import { Button, Checkbox, LoadingIndicator } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { useContentLibraryOptions } from '../../hooks/useContentLibraryOptions';
import { useFormFieldOptions } from '../../hooks/useFormFieldOptions';
import { FormContext } from '../../hooks/FormContext';
import {
  findMissingRequiredContentLibraryInput,
  hasMissingRequiredContentLibraryInputs,
} from '../../utils/context-requirements';
import { isRuleActive } from '../../utils/validation';
import type { DataSourceConfig, ValidationRules } from '@commenda/types';
import { LabelWithGuidanceTooltip } from './LabelWithGuidanceTooltip';

interface CheckboxOption {
  value: string;
  label: string;
  description?: string;
  helpText?: string;
  defaultChecked?: boolean;
  required?: boolean;
  disabled?: boolean;
  metadata?: Record<string, unknown>;
}

export const CheckboxGroupComponent = ({
  fieldId,
  label,
  description,
  options: staticOptions,
  guidance,
  validation,
  dataSource,
}: {
  fieldId: string;
  label?: string;
  description?: string;
  options?: CheckboxOption[];
  guidance?: { text?: string };
  validation?: {
    rules?: ValidationRules;
    triggers?: string[];
  };
  dataSource?: DataSourceConfig;
}) => {
  const {
    formData,
    updateField,
    validateField,
    errors,
    dslContext,
    isFieldVisible,
  } = useContext(FormContext);
  const values = (formData[fieldId] as Record<string, boolean>) || {};

  const isDynamicDataSource =
    dataSource?.type === 'content-library' && dataSource.contentLibrary;

  const isFormFieldDataSource =
    dataSource?.type === 'form-field' && !!dataSource.formField;

  const { options: formFieldOptions } = useFormFieldOptions({
    config: dataSource?.formField,
    formData,
    skip: !isFormFieldDataSource,
  });

  const {
    options: dynamicOptions,
    isLoading,
    isLoadingMore,
    error: optionsError,
    hasMore,
    loadMore,
  } = useContentLibraryOptions({
    config: dataSource?.contentLibrary,
    dslContext,
    formData,
    skip: !isDynamicDataSource,
  });

  const hasMissingInputs = useMemo(() => {
    if (!isDynamicDataSource) {
      return false;
    }
    return hasMissingRequiredContentLibraryInputs(
      dataSource?.contentLibrary?.inputs,
      dslContext,
      formData
    );
  }, [
    isDynamicDataSource,
    dataSource?.contentLibrary?.inputs,
    dslContext,
    formData,
  ]);

  const options: CheckboxOption[] = isFormFieldDataSource
    ? (formFieldOptions as CheckboxOption[])
    : isDynamicDataSource
      ? (dynamicOptions as CheckboxOption[])
      : staticOptions || [];
  const isRequired = isRuleActive(validation?.rules?.required, isFieldVisible);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (!options || options.length === 0 || initializedRef.current) return;

    const mandatoryOptions = options.filter(
      (opt) => opt.metadata?.mandatory === true
    );

    if (mandatoryOptions.length > 0) {
      const newValues = { ...values };
      let hasChanges = false;

      for (const opt of mandatoryOptions) {
        if (!newValues[opt.value]) {
          newValues[opt.value] = true;
          hasChanges = true;
        }
      }

      if (hasChanges) {
        updateField(fieldId, newValues);
        initializedRef.current = true;
      }
    }
  }, [options, fieldId, values, updateField]);

  const handleChange = (optionValue: string, checked: boolean) => {
    const newValues: Record<string, boolean> = {
      ...values,
      [optionValue]: checked,
    };
    updateField(fieldId, newValues);
    if (validation?.rules) {
      validateField(fieldId, newValues, validation.rules);
    }
  };

  const isOptionMandatory = (option: CheckboxOption): boolean => {
    return option.metadata?.mandatory === true;
  };

  const isOptionDisabled = (option: CheckboxOption): boolean => {
    return option.disabled === true || isOptionMandatory(option);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 py-2 text-text-tertiary">
          <LoadingIndicator size={16} />
          <span className="txt-small-regular">Loading options...</span>
        </div>
      );
    }

    if (hasMissingInputs) {
      const missingInput = findMissingRequiredContentLibraryInput(
        dataSource?.contentLibrary?.inputs,
        dslContext,
        formData
      );
      const missingLabel =
        missingInput?.source === 'field' && missingInput.fieldId
          ? `Select ${missingInput.fieldId} first`
          : 'Waiting for required input...';

      return (
        <div className="py-2 text-text-tertiary txt-small-regular">
          {missingLabel}
        </div>
      );
    }

    if (optionsError) {
      return (
        <div className="py-2 text-text-error txt-small-regular">
          {optionsError.message}
        </div>
      );
    }

    if (options.length === 0) {
      return (
        <div className="py-2 text-text-tertiary txt-small-regular">
          No options available
        </div>
      );
    }

    return (
      <>
        <div className="space-y-3">
          {options.map((option) => {
            const isMandatory = isOptionMandatory(option);
            const isDisabled = isOptionDisabled(option);
            const hasUserValue = values[option.value] !== undefined;
            const isChecked = hasUserValue
              ? values[option.value]
              : option.defaultChecked || isMandatory || false;
            const optionDescription = option.description || option.helpText;

            return (
              <label
                key={option.value}
                className={cn(
                  'flex items-start',
                  isDisabled
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                )}
              >
                <Checkbox
                  checked={isChecked}
                  onCheckedChange={(checked) =>
                    handleChange(option.value, checked === true)
                  }
                  disabled={isDisabled}
                  className={cn('mt-0.5 mr-3', isDisabled && 'cursor-not-allowed')}
                />
                <div className="flex flex-col">
                  <span
                    className={cn(
                      'txt-small-regular text-text-secondary',
                      option.required || isMandatory ? 'font-medium' : ''
                    )}
                  >
                    {option.label}
                    {option.required && (
                      <span className="text-text-error ml-1">*</span>
                    )}
                    {isMandatory && (
                      <span className="text-text-tertiary ml-2 txt-xs-regular">
                        (Required)
                      </span>
                    )}
                  </span>
                  {optionDescription && (
                    <span className="txt-xs-regular text-text-quaternary mt-0.5">
                      {optionDescription}
                    </span>
                  )}
                </div>
              </label>
            );
          })}
        </div>
        {isDynamicDataSource && hasMore && (
          <div className="mt-2">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={loadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <div className="flex items-center gap-2">
                  <LoadingIndicator size={16} />
                  <span>Loading...</span>
                </div>
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="mb-4" data-field-id={fieldId}>
      {(label || description) && (
        <LabelWithGuidanceTooltip
          label={label}
          description={description}
          guidance={guidance?.text}
          isRequired={isRequired}
        />
      )}
      {renderContent()}
      {errors[fieldId] && (
        <p className="mt-1 text-sm text-text-error">{errors[fieldId]}</p>
      )}
    </div>
  );
};
